import { Chart, ChartConfiguration, ScriptableContext } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import {
  formatChartDayLabel,
  HeatPlanGraphViewModel,
} from './heat-plan-graph-parser.util';
import { createExternalTooltipHandler } from './heat-plan-chart-tooltip.util';

Chart.register(annotationPlugin);

export type HeatPlanChartRole = 'original' | 'uploaded';

export const HEAT_PLAN_CHART_THEME = {
  tick: '#94a3b8',
  grid: 'rgba(56, 189, 248, 0.06)',
  gridStrong: 'rgba(56, 189, 248, 0.12)',
  legend: '#cbd5e1',
  surface: '#020617',
  accent: '#38bdf8',
  planA: '#38bdf8',
  planB: '#34d399',
  tanks: {
    original: [
      { dark: '#0369a1', light: '#38bdf8' },
      { dark: '#0e7490', light: '#22d3ee' },
      { dark: '#0f766e', light: '#2dd4bf' },
      { dark: '#4338ca', light: '#818cf8' },
      { dark: '#1d4ed8', light: '#60a5fa' },
      { dark: '#075985', light: '#7dd3fc' },
    ],
    uploaded: [
      { dark: '#065f46', light: '#34d399' },
      { dark: '#047857', light: '#6ee7b7' },
      { dark: '#0f766e', light: '#5eead4' },
      { dark: '#115e59', light: '#2dd4bf' },
      { dark: '#134e4a', light: '#99f6e4' },
      { dark: '#14532d', light: '#4ade80' },
    ],
  },
  line: {
    original: '#38bdf8',
    uploaded: '#34d399',
  },
};

function scaleTemp(values: number[], useFahrenheit: boolean): number[] {
  if (!useFahrenheit) {
    return values;
  }
  return values.map((v) => (v ? (v * 9) / 5 + 32 : 0));
}

function tempUnit(useFahrenheit: boolean): string {
  return useFahrenheit ? '°F' : '°C';
}

function parseLabelDate(label: string): number {
  const m = label.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (!m) {
    return 0;
  }
  return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])).getTime();
}

function chartXLabels(view: HeatPlanGraphViewModel): string[] {
  return view.chartLabels?.length === view.labels.length
    ? view.chartLabels
    : view.labels;
}

function tankGradientPair(
  role: HeatPlanChartRole,
  index: number,
): { dark: string; light: string } {
  const palette =
    role === 'uploaded'
      ? HEAT_PLAN_CHART_THEME.tanks.uploaded
      : HEAT_PLAN_CHART_THEME.tanks.original;
  return palette[index % palette.length];
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function barGradient(
  ctx: ScriptableContext<'bar'>,
  dark: string,
  light: string,
): string | CanvasGradient {
  const chart = ctx.chart;
  const { chartArea, ctx: canvasCtx } = chart;
  if (!chartArea) {
    return light;
  }
  const g = canvasCtx.createLinearGradient(
    0,
    chartArea.bottom,
    0,
    chartArea.top,
  );
  g.addColorStop(0, hexToRgba(dark, 0.55));
  g.addColorStop(0.5, hexToRgba(light, 0.75));
  g.addColorStop(1, light);
  return g;
}

function lineAreaGradient(
  ctx: ScriptableContext<'line'>,
  color: string,
): string | CanvasGradient {
  const chart = ctx.chart;
  const { chartArea, ctx: canvasCtx } = chart;
  if (!chartArea) {
    return 'rgba(56, 189, 248, 0.15)';
  }
  const g = canvasCtx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom,
  );
  const rgb = color === '#34d399' ? '52, 211, 153' : '56, 189, 248';
  g.addColorStop(0, `rgba(${rgb}, 0.42)`);
  g.addColorStop(0.65, `rgba(${rgb}, 0.08)`);
  g.addColorStop(1, `rgba(${rgb}, 0)`);
  return g;
}

function computeYBounds(
  view: HeatPlanGraphViewModel,
  useFahrenheit: boolean,
): { min: number; max: number; avg: number } {
  const temps = view.tankSeries
    .flatMap((t) => scaleTemp(t.data, useFahrenheit))
    .filter((v) => v > 0);
  const load = scaleTemp(view.loadTempLine, useFahrenheit).filter((v) => v > 0);
  const all = [...temps, ...load];
  if (!all.length) {
    return { min: 0, max: 80, avg: 0 };
  }
  const min = Math.min(...all);
  const max = Math.max(...all);
  const avg = load.length
    ? load.reduce((a, b) => a + b, 0) / load.length
    : temps.reduce((a, b) => a + b, 0) / temps.length;
  const pad = Math.max(4, (max - min) * 0.12);
  return {
    min: Math.max(0, Math.floor(min - pad)),
    max: Math.ceil(max + pad),
    avg,
  };
}

function buildAnnotations(
  avg: number,
  unit: string,
  role: HeatPlanChartRole,
): Record<string, unknown> {
  if (!avg || avg <= 0) {
    return {};
  }
  const color =
    role === 'uploaded'
      ? 'rgba(52, 211, 153, 0.55)'
      : 'rgba(56, 189, 248, 0.55)';
  return {
    avgLine: {
      type: 'line',
      yMin: avg,
      yMax: avg,
      borderColor: color,
      borderWidth: 1,
      borderDash: [8, 6],
      label: {
        display: true,
        content: `7-day avg ${avg.toFixed(1)} ${unit}`,
        position: 'start',
        backgroundColor: 'rgba(2, 6, 23, 0.85)',
        color: '#e2e8f0',
        font: { size: 11, weight: 'bold' },
        padding: { top: 4, bottom: 4, left: 8, right: 8 },
        borderRadius: 6,
      },
    },
  };
}

function chartOptions(
  unit: string,
  yBounds: { min: number; max: number },
  tooltipParent: HTMLElement | null,
  annotations: Record<string, unknown>,
  showLegend = false,
  compact = false,
): ChartConfiguration<'bar' | 'line'>['options'] {
  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: compact
        ? { top: 8, right: 8, bottom: 4, left: 4 }
        : { top: 16, right: 20, bottom: 8, left: 8 },
    },
    animation: {
      duration: 700,
      easing: 'easeOutQuart',
    },
    interaction: {
      mode: 'index',
      intersect: false,
      axis: 'x',
    },
    plugins: {
      legend: {
        display: showLegend,
        position: 'bottom',
        labels: {
          boxWidth: 8,
          padding: 14,
          font: { size: 11 },
          color: HEAT_PLAN_CHART_THEME.legend,
          usePointStyle: true,
        },
      },
      tooltip: {
        enabled: false,
        external: createExternalTooltipHandler(tooltipParent, unit),
      },
      annotation: {
        annotations: annotations as never,
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#cbd5e1',
          autoSkip: false,
          font: { size: compact ? 10 : 12, weight: 500 },
          padding: compact ? 4 : 8,
          maxRotation: 0,
        },
        grid: { display: false },
        border: { display: false },
      },
      y: {
        min: yBounds.min,
        max: yBounds.max,
        ticks: {
          color: HEAT_PLAN_CHART_THEME.tick,
          font: { size: 11 },
          maxTicksLimit: 6,
          padding: 10,
        },
        grid: {
          color: (ctx) =>
            ctx.tick.value === yBounds.min || ctx.tick.value === yBounds.max
              ? HEAT_PLAN_CHART_THEME.gridStrong
              : HEAT_PLAN_CHART_THEME.grid,
          lineWidth: 1,
        },
        border: { display: false },
      },
    },
  };
}

export function alignPlansForComparison(
  original: HeatPlanGraphViewModel,
  uploaded: HeatPlanGraphViewModel,
): { labels: string[]; original: HeatPlanGraphViewModel; uploaded: HeatPlanGraphViewModel } {
  const labelSet = new Map<string, number>();
  [...original.labels, ...uploaded.labels].forEach((l) => {
    labelSet.set(l, parseLabelDate(l));
  });

  const sortedLabels = [...labelSet.entries()]
    .sort((a, b) => a[1] - b[1])
    .map(([l]) => l);

  const labels = sortedLabels.slice(-7);
  const chartLabels = labels.map((l) =>
    formatChartDayLabel(parseLabelDate(l)),
  );

  const remap = (view: HeatPlanGraphViewModel): HeatPlanGraphViewModel => {
    const labelIndex = new Map(view.labels.map((l, i) => [l, i]));

    const tankSeries = view.tankSeries.map((t) => ({
      ...t,
      data: labels.map((l) => {
        const idx = labelIndex.get(l);
        return idx !== undefined ? t.data[idx] ?? 0 : 0;
      }),
    }));

    const lineAt = (line: number[]) =>
      labels.map((l) => {
        const idx = labelIndex.get(l);
        return idx !== undefined ? line[idx] ?? 0 : 0;
      });

    return {
      ...view,
      labels,
      chartLabels,
      tankSeries,
      loadTempLine: lineAt(view.loadTempLine),
      voyRangeMinLine: lineAt(view.voyRangeMinLine),
      voyRangeMaxLine: lineAt(view.voyRangeMaxLine),
      voyRangeLabels: labels.map(() => ''),
    };
  };

  return {
    labels,
    original: remap(original),
    uploaded: remap(uploaded),
  };
}

export function buildOverviewChartConfig(
  view: HeatPlanGraphViewModel,
  useFahrenheit = false,
  role: HeatPlanChartRole = 'original',
  tooltipParent: HTMLElement | null = null,
  compact = false,
): ChartConfiguration<'bar' | 'line'> {
  const unit = tempUnit(useFahrenheit);
  const lineColor = HEAT_PLAN_CHART_THEME.line[role];
  const yBounds = computeYBounds(view, useFahrenheit);
  const datasets: ChartConfiguration<'bar' | 'line'>['data']['datasets'] = [];

  view.tankSeries.slice(0, 6).forEach((tank, i) => {
    const pair = tankGradientPair(role, i);
    datasets.push({
      type: 'bar',
      label: tank.label,
      data: scaleTemp(tank.data, useFahrenheit),
      backgroundColor: (ctx) => barGradient(ctx, pair.dark, pair.light),
      borderColor: 'transparent',
      borderWidth: 0,
      borderRadius: { topLeft: 8, topRight: 8, bottomLeft: 2, bottomRight: 2 },
      borderSkipped: false,
      maxBarThickness: 22,
      barPercentage: 0.92,
      categoryPercentage: 0.72,
      yAxisID: 'y',
      order: 2,
    });
  });

  if (view.hasLoadTemp) {
    datasets.push({
      type: 'line',
      label: `Weighted avg (${unit})`,
      data: scaleTemp(view.loadTempLine, useFahrenheit),
      borderColor: lineColor,
      backgroundColor: (ctx) => lineAreaGradient(ctx, lineColor),
      borderWidth: 3.5,
      fill: true,
      pointRadius: 5,
      pointHoverRadius: 8,
      pointBackgroundColor: '#f8fafc',
      pointBorderColor: lineColor,
      pointBorderWidth: 3,
      pointHoverBorderWidth: 3,
      yAxisID: 'y',
      order: 0,
      tension: 0.42,
    });
  }

  return {
    type: 'bar',
    data: { labels: chartXLabels(view), datasets },
    options: chartOptions(
      unit,
      yBounds,
      tooltipParent,
      buildAnnotations(yBounds.avg, unit, role),
      false,
      compact,
    ),
  };
}

export function buildTankPairComparisonChart(
  original: HeatPlanGraphViewModel,
  uploaded: HeatPlanGraphViewModel,
  useFahrenheit = false,
  tooltipParent: HTMLElement | null = null,
): ChartConfiguration<'bar'> {
  const unit = tempUnit(useFahrenheit);
  const aligned = alignPlansForComparison(original, uploaded);
  const datasets: ChartConfiguration<'bar'>['data']['datasets'] = [];
  const tankCount = Math.min(
    aligned.original.tankSeries.length,
    aligned.uploaded.tankSeries.length,
    6,
  );

  for (let t = 0; t < tankCount; t++) {
    const origTank = aligned.original.tankSeries[t];
    const upTank = aligned.uploaded.tankSeries[t];
    datasets.push({
      label: `${origTank.label} (Master Log)`,
      data: scaleTemp(origTank.data, useFahrenheit),
      backgroundColor: 'rgba(56, 189, 248, 0.75)',
      borderColor: '#38bdf8',
      borderWidth: 0,
      borderRadius: 6,
      maxBarThickness: 18,
      barPercentage: 0.9,
      categoryPercentage: 0.65,
    });
    datasets.push({
      label: `${upTank.label} (Comparison)`,
      data: scaleTemp(upTank.data, useFahrenheit),
      backgroundColor: 'rgba(52, 211, 153, 0.75)',
      borderColor: '#34d399',
      borderWidth: 0,
      borderRadius: 6,
      maxBarThickness: 18,
      barPercentage: 0.9,
      categoryPercentage: 0.65,
    });
  }

  const yBounds = computeYBounds(aligned.original, useFahrenheit);

  return {
    type: 'bar',
    data: { labels: chartXLabels(aligned.original), datasets },
    options: chartOptions(unit, yBounds, tooltipParent, {}, false, true),
  };
}

function computeLoadCompareYBounds(
  original: HeatPlanGraphViewModel,
  uploaded: HeatPlanGraphViewModel,
  useFahrenheit: boolean,
): { min: number; max: number } {
  const aligned = alignPlansForComparison(original, uploaded);
  const all = [
    ...scaleTemp(aligned.original.loadTempLine, useFahrenheit),
    ...scaleTemp(aligned.uploaded.loadTempLine, useFahrenheit),
  ].filter((v) => v > 0);
  if (!all.length) {
    return { min: 0, max: 80 };
  }
  const min = Math.min(...all);
  const max = Math.max(...all);
  const pad = Math.max(4, (max - min) * 0.12);
  return {
    min: Math.max(0, Math.floor(min - pad)),
    max: Math.ceil(max + pad),
  };
}

export function buildLoadTempComparisonChart(
  original: HeatPlanGraphViewModel,
  uploaded: HeatPlanGraphViewModel,
  useFahrenheit = false,
  tooltipParent: HTMLElement | null = null,
): ChartConfiguration<'line'> {
  const unit = tempUnit(useFahrenheit);
  const aligned = alignPlansForComparison(original, uploaded);

  return {
    type: 'line',
    data: {
      labels: chartXLabels(aligned.original),
      datasets: [
        {
          label: 'Master Log',
          data: scaleTemp(aligned.original.loadTempLine, useFahrenheit),
          borderColor: HEAT_PLAN_CHART_THEME.planA,
          backgroundColor: (ctx) =>
            lineAreaGradient(ctx, HEAT_PLAN_CHART_THEME.planA),
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 5,
          pointBackgroundColor: '#f8fafc',
          pointBorderColor: HEAT_PLAN_CHART_THEME.planA,
          pointBorderWidth: 2,
        },
        {
          label: 'Comparison Log',
          data: scaleTemp(aligned.uploaded.loadTempLine, useFahrenheit),
          borderColor: HEAT_PLAN_CHART_THEME.planB,
          backgroundColor: (ctx) =>
            lineAreaGradient(ctx, HEAT_PLAN_CHART_THEME.planB),
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 5,
          pointBackgroundColor: '#f8fafc',
          pointBorderColor: HEAT_PLAN_CHART_THEME.planB,
          pointBorderWidth: 2,
        },
      ],
    },
    options: chartOptions(
      unit,
      computeLoadCompareYBounds(original, uploaded, useFahrenheit),
      tooltipParent,
      {},
      false,
      true,
    ),
  };
}

export function buildVoyRangeComparisonChart(
  original: HeatPlanGraphViewModel,
  uploaded: HeatPlanGraphViewModel,
  useFahrenheit = false,
  tooltipParent: HTMLElement | null = null,
): ChartConfiguration<'line'> {
  const unit = tempUnit(useFahrenheit);
  const aligned = alignPlansForComparison(original, uploaded);

  return {
    type: 'line',
    data: {
      labels: chartXLabels(aligned.original),
      datasets: [
        {
          label: 'Master Log min',
          data: scaleTemp(aligned.original.voyRangeMinLine, useFahrenheit),
          borderColor: 'rgba(56, 189, 248, 0.4)',
          borderWidth: 1.5,
          borderDash: [4, 4],
          pointRadius: 0,
          fill: false,
        },
        {
          label: 'Master Log max',
          data: scaleTemp(aligned.original.voyRangeMaxLine, useFahrenheit),
          borderColor: HEAT_PLAN_CHART_THEME.planA,
          borderWidth: 2,
          pointRadius: 3,
          fill: '-1',
          backgroundColor: 'rgba(56, 189, 248, 0.12)',
        },
      ],
    },
    options: chartOptions(unit, { min: 0, max: 100 }, tooltipParent, {}, true),
  };
}
